import React, { useEffect, useMemo, useImperativeHandle } from 'react';
import { Dashboard } from '@uppy/react';
import { Uppy } from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import ImageEditor from '@uppy/image-editor';
import '@uppy/image-editor/dist/style.css';
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';
import PropTypes from 'prop-types';

const Dropzone = (dropZoneProps) => {
    const {
        dataLwtId,
        maxFileSizeInBytes,
        maxNumberOfFiles,
        minNumberOfFiles,
        allowedFileTypes,
        autoProceed,
        dropFilesMessage,
        endpoint,
        fileMetadata,
        headers,
        onFileAdded,
        onFileRemoved,
        onSuccess,
        onFailure,
        onBeforeFileAdded,
        uppyRef,
        onEditorStart,
        onEditorCancel,
        onEditorComplete,
        imageEditorOptions,
        onUpload,
        ...restProps
    } = dropZoneProps;

    useEffect(() => {
        if (fileMetadata && fileMetadata.id) {
            uppy.setFileMeta(fileMetadata.id, fileMetadata.metadata ? fileMetadata.metadata : {});
        }
    }, [fileMetadata]);

    useEffect(() => {
        return () => {
            uppy.cancelAll();
        };
    }, []);

    const defaultEditorActions = {
        revert: false,
        rotate: false,
        flip: false,
        zoomIn: true,
        zoomOut: true,
        cropSquare: true,
        cropWidescreen: true,
        cropWidescreenVertical: true,
        granularRotate: false,
        ...imageEditorOptions?.actions
    };

    const defaultCropperOption = {
        background: true,
        croppedCanvasOptions: {},
        ...imageEditorOptions?.cropperOptions
    };

    // using a memo to fix an issue when listening to an event that may cause a rerender
    // https://github.com/transloadit/uppy/issues/2473
    const uppy = useMemo(() => {
        const newUppy = new Uppy({
            debug: false,
            autoProceed: autoProceed,
            restrictions: {
                maxFileSize: maxFileSizeInBytes,
                maxNumberOfFiles: maxNumberOfFiles,
                minNumberOfFiles: minNumberOfFiles,
                allowedFileTypes: allowedFileTypes
            },
            onBeforeFileAdded(currentFile, files) {
                if (onBeforeFileAdded) {
                    return onBeforeFileAdded(currentFile, files);
                }
            }
        });

        if (endpoint) {
            newUppy.use(XHRUpload, {
                endpoint: endpoint,
                formData: true
            });
        }

        newUppy
            .on('upload', () => {
                if(onUpload) {
                    onUpload(uppy.getFiles());
                    return;
                }
                if (headers) {
                    newUppy.getPlugin('XHRUpload')?.setOptions({
                        headers: headers
                    });
                }
            })
            .on('file-added', (file) => {
                if (onFileAdded) {
                    onFileAdded(file);
                }
            })
            .on('file-removed', (file) => {
                if (onFileRemoved) {
                    onFileRemoved(file);
                }
            })
            .on('complete', (result) => {
                if (result.successful && onSuccess) {
                    onSuccess(result.successful);
                }
                if (result.failed && onFailure) {
                    onFailure(result.failed);
                }
            })
            .use(ImageEditor, {
                actions: defaultEditorActions,
                cropperOptions: {
                    ...defaultCropperOption,
                    croppedCanvasOptions: {
                        ...defaultCropperOption.croppedCanvasOptions
                    }
                }
            })
            .on('file-editor:start', (file) => {
                if (onEditorStart) {
                    onEditorStart(file);
                }
            })
            .on('file-editor:complete', (file) => {
                if (onEditorComplete) {
                    onEditorComplete(file);
                }
            })
            .on('file-editor:cancel', (file) => {
                if (onEditorCancel) {
                    onEditorCancel(file);
                }
            })
        ;

        return newUppy;
    }, [autoProceed, maxFileSizeInBytes, maxNumberOfFiles, minNumberOfFiles, allowedFileTypes, onBeforeFileAdded,
        endpoint, headers, onUpload, onFileAdded, onFileRemoved, onSuccess, onFailure, onEditorStart, onEditorComplete,
        onEditorCancel, imageEditorOptions]);

    useImperativeHandle(uppyRef, () => ({
        upload: uppy.upload,
        addFile: (file) => uppy.addFile({ data: file }),
        removeFile: (file) => uppy.removeFile(file.id),
        reset: () => uppy.reset(),
        getFiles: () => uppy.getFiles()
    }));

    const locale = { strings: {} };
    if (dropFilesMessage) {
        locale.strings['dropPaste'] = dropFilesMessage;
    }

    return (
        <div data-lwt-id={dataLwtId}>
            <Dashboard uppy={uppy} proudlyDisplayPoweredByUppy={false} locale={locale} {...restProps} />
        </div>
    );
};

Dropzone.propTypes = {
    dataLwtId: PropTypes.string,
    maxFileSizeInBytes: PropTypes.number,
    maxNumberOfFiles: PropTypes.number,
    minNumberOfFiles: PropTypes.number,
    allowedFileTypes: PropTypes.arrayOf(PropTypes.string),
    autoProceed: PropTypes.bool,
    dropFilesMessage: PropTypes.string,
    endpoint: PropTypes.string,
    fileMetadata: PropTypes.object,
    headers: PropTypes.object,
    onFileAdded: PropTypes.func,
    onFileRemoved: PropTypes.func,
    onSuccess: PropTypes.func,
    onFailure: PropTypes.func,
    onBeforeFileAdded: PropTypes.func,
    uppyRef: PropTypes.shape({
        current: PropTypes.object
    }),
    onEditorStart: PropTypes.func,
    onEditorCancel: PropTypes.func,
    onEditorComplete: PropTypes.func,
    imageEditorOptions: PropTypes.shape({
        actions: PropTypes.object,
        cropperOptions: PropTypes.object
    }),
    onUpload: PropTypes.func
};

export default Dropzone;
